// src/hooks/sale/useSaleDtePreview.ts
import { useState } from 'react';
import { blobToBase64 } from '../../utils/blobToBase64';
import SalePdfService from '../../services/sale/SalePdfService';
import { useSale } from './useSale';
import useLoading from '../useLoading';

/**
 * 📄 Hook para obtener el PDF DTE de una venta (incluso anulada), codificado en base64.
 *
 * ✅ Usa `SalePdfService.view` internamente, sin necesidad de tipo (ticket, etc.)
 * ✅ Muestra loading mientras se descarga
 * ✅ Abre automáticamente el modal al obtener el DTE
 */
export const useSaleDtePreview = () => {
  const [base64Dte, setBase64Dte] = useState<string | undefined>();
  const { openDtePdfModal } = useSale();
  const { showLoadingIndicator, hideLoadingIndicator } = useLoading();

  /**
   * Obtiene el PDF de una venta por su ID, lo convierte a base64 y lo guarda en estado.
   * Si el PDF es válido, abre el modal automáticamente.
   *
   * @param saleId ID de la venta
   */
  const handleViewDte = async (saleId: number) => {
    if (!saleId) {
      console.warn('⚠️ No se proporcionó el ID de la venta');
      return;
    }

    try {
      showLoadingIndicator(); // ✅ Mostrar loading mientras carga el PDF

      const { blob } = await SalePdfService.view(saleId);
      if (blob) {
        const base64 = await blobToBase64(blob);
        setBase64Dte(base64);
        openDtePdfModal(); // ✅ Abre modal con el PDF cargado
      } else {
        console.warn('⚠️ No se recibió el PDF del servidor');
      }
    } catch (err) {
      console.error('❌ Error al obtener el DTE:', err);
    } finally {
      hideLoadingIndicator(); // ✅ Ocultar loading siempre
    }
  };

  return {
    base64Dte,
    setBase64Dte,
    handleViewDte,
  };
};
